import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const parse = (v) => { try { return v ? parseISO(String(v).slice(0, 10)) : undefined; } catch { return undefined; } };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

// Pemilih bulan seragam (value & onChange "YYYY-MM")
export const MonthPick = ({ value, onChange, testid, disabled, className = "", size = "md", min, max }) => {
  const [open, setOpen] = useState(false);
  const [y, setY] = useState(() => +(value || new Date().toISOString()).slice(0, 4));
  const h = size === "sm" ? "h-8 text-xs rounded-full" : size === "pill" ? "h-9 text-sm rounded-full" : "h-10 text-sm rounded-lg";
  const label = value ? `${FULL[+value.slice(5, 7) - 1]} ${value.slice(0, 4)}` : "Pilih bulan";
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} disabled={disabled} className={`${className.includes("w-") ? "" : "w-44"} ${h} border border-slate-200 bg-white px-3 inline-flex items-center gap-2 hover:border-slate-400 transition-colors disabled:opacity-50 whitespace-nowrap ${className}`}>
          <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" /><span className="flex-1 text-left">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="flex items-center justify-between mb-2"><button type="button" className="px-2 text-slate-500" onClick={() => setY(y - 1)} data-testid={`${testid}-prev`}>‹</button><b className="text-sm">{y}</b><button type="button" className="px-2 text-slate-500" onClick={() => setY(y + 1)} data-testid={`${testid}-next`}>›</button></div>
        <div className="grid grid-cols-3 gap-1">{MONTHS.map((m, i) => { const v = `${y}-${String(i + 1).padStart(2, "0")}`; const off = (min && v < min) || (max && v > max); return (
          <button key={m} type="button" disabled={off} data-testid={`${testid}-m${i + 1}`} onClick={() => { onChange(v); setOpen(false); }} className={`py-1.5 rounded-md text-sm ${v === value ? "bg-slate-900 text-white" : "hover:bg-slate-100"} disabled:opacity-30`}>{m}</button>); })}</div>
      </PopoverContent>
    </Popover>
  );
};

// Pemilih jam seragam (value & onChange "HH:MM", menit per 5)
export const TimePick = ({ value, onChange, testid, disabled, className = "" }) => {
  const [hh, mm] = (value || "").split(":");
  const sel = "h-10 rounded-lg border border-slate-200 bg-white px-1 text-sm disabled:opacity-50";
  const set = (a, b) => onChange(`${a || "08"}:${b || "00"}`);
  const mins = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));
  if (mm && !mins.includes(mm)) mins.push(mm);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`} data-testid={testid}>
      <select disabled={disabled} data-testid={`${testid}-h`} value={hh || ""} onChange={(e) => set(e.target.value, mm)} className={sel}><option value="">--</option>{Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0")).map((x) => <option key={x}>{x}</option>)}</select>
      <span className="text-slate-400">:</span>
      <select disabled={disabled} data-testid={`${testid}-m`} value={mm || ""} onChange={(e) => set(hh, e.target.value)} className={sel}><option value="">--</option>{mins.sort().map((x) => <option key={x}>{x}</option>)}</select>
    </span>
  );
};

// Pemilih tanggal kalender seragam (value & onChange memakai string YYYY-MM-DD)
export const DatePick = ({ value, onChange, testid, placeholder = "Pilih tanggal", min, max, disabled, className = "", size = "md", clearable = false }) => {
  const [open, setOpen] = useState(false);
  const d = parse(value); const lo = parse(min); const hi = parse(max);
  const h = size === "sm" ? "h-8 text-xs rounded-full" : size === "pill" ? "h-9 text-sm rounded-full" : "h-10 text-sm rounded-lg";
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} disabled={disabled} className={`${className.includes("w-") ? "" : "w-full"} ${h} border border-slate-200 bg-white px-3 text-left inline-flex items-center gap-2 hover:border-slate-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ${className}`}>
          <CalendarDays className="w-4 h-4 text-slate-400 shrink-0" />
          <span className={`flex-1 ${d ? "text-slate-900" : "text-slate-400"}`}>{d ? format(d, "d MMM yyyy", { locale: localeId }) : placeholder}</span>
          {clearable && d && <X className="w-3.5 h-3.5 text-slate-400 hover:text-rose-500" data-testid={`${testid}-clear`} onClick={(e) => { e.stopPropagation(); onChange(""); }} />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={d} defaultMonth={d || lo} locale={localeId} disabled={[...(lo ? [{ before: lo }] : []), ...(hi ? [{ after: hi }] : [])]}
          onSelect={(x) => { if (x) { onChange(format(x, "yyyy-MM-dd")); setOpen(false); } }} initialFocus />
      </PopoverContent>
    </Popover>
  );
};
